/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { createAction } from '../../utils/redux-utils';
import {
    CompanyDetailsDto,
    CompanyStoresListDto,
    CompanyStoresListFilterParams,
} from '../api/companies/companiesClient.typings';
import { Notification } from '../../utils/utils';
import { CashBoxesStats } from '../api/cashBoxes/cashBoxesClient.typings';
import {
    AiClassificationsHistoryDto,
    PageQueryParams,
    PeriodQueryParams,
    RegistryTurnoversByObjectsDto,
    TurnoversByPeriodEXCELParams,
    TurnoversByPeriodQueryParams,
    TurnoversByPeriodWithParams,
} from '../api/apiCommon.typings';
import { TurnoverReportType } from '../../components/RegistryWidgets/Common/ListTypes';

export enum CompanyDetailsActionType {
    OPEN_REGISTRY_COMPANY_CARD_PAGE = 'RegistryCompanyCard/open page',
    EXIT_REGISTRY_COMPANY_CARD_PAGE = 'RegistryCompanyCard/exit page',

    FETCH_COMPANY_DETAILS_SUCCESS = 'RegistryCompanyCard/ fetch company details success',

    FETCH_COMPANY_STORES_LIST_SUCCESS = 'RegistryCompanyCard/ fetch company stores list success',
    APPLY_STORES_FILTER = 'RegistryCompanyCard/ apply stores list filter',
    APPLY_STORES_PAGING = 'RegistryCompanyCard/ apply stores list paging',

    FETCH_COMPANY_CASHBOXES_STATS = 'RegistryCompanyCard/ fetch company cashboxes statistics',
    FETCH_COMPANY_CASHBOXES_STATS_SUCCESS = 'RegistryCompanyCard/ fetch company cashboxes stats success',
    FETCH_COMPANY_CASHBOXES_STATS_ERROR = 'RegistryCompanyCard/ fetch company cashboxes statistics error',

    FETCH_AI_CLASSIFICATIONS_HISTORY_START = 'RegistryCompanyCard/ fetch ai classifications history start',
    FETCH_AI_CLASSIFICATIONS_HISTORY_SUCCESS = 'RegistryCompanyCard/ fetch ai classifications history success',
    FETCH_AI_CLASSIFICATIONS_HISTORY_ERROR = 'RegistryCompanyCard/ fetch ai classifications history error',
    DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_START = 'RegistryCompanyCard/ download ai classifications history start',
    DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_SUCCESS = 'RegistryCompanyCard/ download ai classifications history success',
    DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_ERROR = 'RegistryCompanyCard/ download ai classifications history error',

    CHANGE_HISTORY_TAB = 'RegistryCompanyCard/ change history tab',
    FETCH_COMPANY_CARD_DATA_ERROR = 'RegistryCompanyCard/ fetch company card data error',

    SET_TURNOVERS_HIDE_TOGGLE = 'RegistryCompanyCard/ set turnovers table hide toggle',
    SET_TURNOVERS_REPORT_TYPE = 'RegistryCompanyCard/ set turnovers report type',
    FETCH_COMPANY_TURNOVERS_BY_PERIOD = 'RegistryCompanyCard/ fetch company turnovers by period',
    FETCH_COMPANY_TURNOVERS_BY_PERIOD_SUCCESS = 'RegistryCompanyCard/ fetch company turnovers by period success',
    FETCH_COMPANY_TURNOVERS_BY_PERIOD_ERROR = 'RegistryCompanyCard/ fetch company turnovers by period error',
    COLLAPSE_TURNOVERS_BY_PERIOD = 'RegistryCompanyCard/ collapse turnovers by period',
    EXPAND_TURNOVERS_PERIOD = 'RegistryCompanyCard/ expand turnovers period',
    DOWNLOAD_TURNOVERS_BY_PERIOD = 'RegistryCompanyCard/ download turnovers by period as excel',
    DOWNLOAD_TURNOVERS_BY_PERIOD_SUCCESS = 'RegistryCompanyCard/ download turnovers by period as excel success',
    DOWNLOAD_TURNOVERS_BY_PERIOD_ERROR = 'RegistryCompanyCard/ download turnovers by period as excel error',

    FETCH_COMPANY_TURNOVERS_BY_OBJECTS = 'RegistryCompanyCard/ fetch company turnovers by objects',
    FETCH_COMPANY_TURNOVERS_BY_OBJECTS_SUCCESS = 'RegistryCompanyCard/ fetch company turnovers by objects success',
    FETCH_COMPANY_TURNOVERS_BY_OBJECTS_ERROR = 'RegistryCompanyCard/ fetch company turnovers by objects error',
    APPLY_TURNOVERS_BY_OBJECTS_FILTER = 'RegistryCompanyCard/ apply filter for turnovers by objects',
    APPLY_TURNOVERS_BY_OBJECTS_PAGING = 'RegistryCompanyCard/ apply paging for turnovers by objects',
    DOWNLOAD_TURNOVERS_BY_OBJECTS = 'RegistryCompanyCard/ download turnovers by objects as excel',
    DOWNLOAD_TURNOVERS_BY_OBJECTS_SUCCESS = 'RegistryCompanyCard/ download turnovers by objects as excel success',
    DOWNLOAD_TURNOVERS_BY_OBJECTS_ERROR = 'RegistryCompanyCard/ download turnovers by objects as excel error',
}

export const openRegistryCompanyCardAction = (companyTaxNumber: string) =>
    createAction(CompanyDetailsActionType.OPEN_REGISTRY_COMPANY_CARD_PAGE, companyTaxNumber);

export const exitRegistryCompanyCardAction = () =>
    createAction(CompanyDetailsActionType.EXIT_REGISTRY_COMPANY_CARD_PAGE);

export const fetchCompanyDetailsSuccessAction = (payload: CompanyDetailsDto) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_DETAILS_SUCCESS, payload);

export const fetchCompanyStoresListSuccessAction = (payload: CompanyStoresListDto) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_STORES_LIST_SUCCESS, payload);

export const applyStoresPagingAction = (page: PageQueryParams) =>
    createAction(CompanyDetailsActionType.APPLY_STORES_PAGING, page);

export const applyStoresFilteringAction = (params: CompanyStoresListFilterParams) =>
    createAction(CompanyDetailsActionType.APPLY_STORES_FILTER, params);

export const fetchCompanyCashBoxesStatsAction = () =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS);

export const fetchCompanyCashboxesStatsSuccessAction = (payload: CashBoxesStats) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS_SUCCESS, payload);

export const fetchCompanyCashBoxesStatsErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_CASHBOXES_STATS_ERROR, { notification });

export const fetchCompanyClassificationsHistoryStartAction = (params: PageQueryParams) =>
    createAction(CompanyDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_START, params);

export const fetchCompanyClassificationsHistorySuccessAction = (payload: AiClassificationsHistoryDto) =>
    createAction(CompanyDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_SUCCESS, payload);

export const fetchCompanyClassificationsHistoryErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.FETCH_AI_CLASSIFICATIONS_HISTORY_ERROR, { notification });

export const downloadCompanyClassificationsHistoryAction = () =>
    createAction(CompanyDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_START);

export const downloadCompanyClassificationsHistorySuccessAction = () =>
    createAction(CompanyDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_SUCCESS);

export const downloadCompanyClassificationsHistoryErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.DOWNLOAD_AI_CLASSIFICATIONS_HISTORY_ERROR, { notification });

export const changeHistoryTabAction = (value: number) =>
    createAction(CompanyDetailsActionType.CHANGE_HISTORY_TAB, value);

export const fetchCompanyCardDataErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_CARD_DATA_ERROR, { notification });

export const setTurnoversHideToggleAction = (isToHide: boolean) =>
    createAction(CompanyDetailsActionType.SET_TURNOVERS_HIDE_TOGGLE, isToHide);

export const setTurnoversReportTypeAction = (reportType: TurnoverReportType) =>
    createAction(CompanyDetailsActionType.SET_TURNOVERS_REPORT_TYPE, reportType);

export const fetchCompanyTurnoversByPeriodAction = (payload: TurnoversByPeriodQueryParams) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD, payload);

export const fetchCompanyTurnoversByPeriodSuccessAction = (payload: TurnoversByPeriodWithParams) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD_SUCCESS, payload);

export const fetchCompanyTurnoversByPeriodErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_PERIOD_ERROR, { notification });

export const collapseTurnoversByPeriodAction = () =>
    createAction(CompanyDetailsActionType.COLLAPSE_TURNOVERS_BY_PERIOD);

export const expandTurnoversPeriodAction = (payload: { period: string; isToHide: boolean }) =>
    createAction(CompanyDetailsActionType.EXPAND_TURNOVERS_PERIOD, payload);

export const downloadTurnoversByPeriodAction = (params: TurnoversByPeriodEXCELParams) =>
    createAction(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD, params);

export const downloadTurnoversByPeriodSuccessAction = () =>
    createAction(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD_SUCCESS);

export const downloadTurnoversByPeriodErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_PERIOD_ERROR, { notification });

export const fetchCompanyTurnoversByObjectsAction = () =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS);

export const fetchCompanyTurnoversByObjectsSuccessAction = (payload: RegistryTurnoversByObjectsDto) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS_SUCCESS, payload);

export const fetchCompanyTurnoversByObjectsErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.FETCH_COMPANY_TURNOVERS_BY_OBJECTS_ERROR, { notification });

export const applyTurnoversByObjectsPagingAction = (params: PageQueryParams) =>
    createAction(CompanyDetailsActionType.APPLY_TURNOVERS_BY_OBJECTS_PAGING, params);

export const applyTurnoversByObjectsFilteringAction = (params: PeriodQueryParams) =>
    createAction(CompanyDetailsActionType.APPLY_TURNOVERS_BY_OBJECTS_FILTER, params);

export const downloadTurnoversByObjectsAction = () =>
    createAction(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS);

export const downloadTurnoversByObjectsSuccessAction = () =>
    createAction(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS_SUCCESS);

export const downloadTurnoversByObjectsErrorAction = (notification: Notification) =>
    createAction(CompanyDetailsActionType.DOWNLOAD_TURNOVERS_BY_OBJECTS_ERROR, { notification });

export type CompanyDetailsActions =
    | ReturnType<typeof openRegistryCompanyCardAction>
    | ReturnType<typeof exitRegistryCompanyCardAction>
    | ReturnType<typeof fetchCompanyDetailsSuccessAction>
    | ReturnType<typeof fetchCompanyStoresListSuccessAction>
    | ReturnType<typeof applyStoresPagingAction>
    | ReturnType<typeof applyStoresFilteringAction>
    | ReturnType<typeof fetchCompanyCashBoxesStatsAction>
    | ReturnType<typeof fetchCompanyCashboxesStatsSuccessAction>
    | ReturnType<typeof fetchCompanyCashBoxesStatsErrorAction>
    | ReturnType<typeof fetchCompanyClassificationsHistoryStartAction>
    | ReturnType<typeof fetchCompanyClassificationsHistorySuccessAction>
    | ReturnType<typeof fetchCompanyClassificationsHistoryErrorAction>
    | ReturnType<typeof downloadCompanyClassificationsHistoryAction>
    | ReturnType<typeof downloadCompanyClassificationsHistorySuccessAction>
    | ReturnType<typeof downloadCompanyClassificationsHistoryErrorAction>
    | ReturnType<typeof changeHistoryTabAction>
    | ReturnType<typeof fetchCompanyCardDataErrorAction>
    | ReturnType<typeof setTurnoversHideToggleAction>
    | ReturnType<typeof setTurnoversReportTypeAction>
    | ReturnType<typeof fetchCompanyTurnoversByPeriodAction>
    | ReturnType<typeof fetchCompanyTurnoversByPeriodSuccessAction>
    | ReturnType<typeof fetchCompanyTurnoversByPeriodErrorAction>
    | ReturnType<typeof collapseTurnoversByPeriodAction>
    | ReturnType<typeof expandTurnoversPeriodAction>
    | ReturnType<typeof downloadTurnoversByPeriodAction>
    | ReturnType<typeof downloadTurnoversByPeriodSuccessAction>
    | ReturnType<typeof downloadTurnoversByPeriodErrorAction>
    | ReturnType<typeof fetchCompanyTurnoversByObjectsAction>
    | ReturnType<typeof fetchCompanyTurnoversByObjectsSuccessAction>
    | ReturnType<typeof fetchCompanyTurnoversByObjectsErrorAction>
    | ReturnType<typeof applyTurnoversByObjectsPagingAction>
    | ReturnType<typeof applyTurnoversByObjectsFilteringAction>
    | ReturnType<typeof downloadTurnoversByObjectsAction>
    | ReturnType<typeof downloadTurnoversByObjectsSuccessAction>
    | ReturnType<typeof downloadTurnoversByObjectsErrorAction>;
